import React from 'react';
import { CalendarIcon, ContactSupportIcon } from '@nebula/icons/ui';
import iconSizes from '@nebula/icons/ui/iconSizes';
import { InfoCardExtended } from '@nebula/cards';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isMobileApp, MobileLocations, navigateTo } from '@ecs-uk/web-common-mobile-view-helpers';
import { DrawerHeading, ActionCardContainer } from './index.styles';
import CreditCardIcon from '../../../Icons/credit-card-24.svg';
import SettingsIcon from '../../../Icons/SettingsIcon';
import { getEnv } from '../../../Context/env';
import useSplits from '../../../../hooks/useSplits';
import { queryParams, splitsList } from '../../../../constants';
import { flexProductFamilies, normalizeProductFamily } from '../../../../constants/products';
import { getFamilyBillingPeriods } from '../../../../helpers/flex-helpers/findFlexPlanByFamily';
import { isAnnually } from '../../../../helpers/flex-helpers/flexBillingFrequency';
import type { ProductPlan, CurrentSubscriptionPlan, OrderedPlan } from '../../types';

interface ManageSubscriptionNavProps {
  isInCoolingOffPeriod: boolean;
  currentSubscription: CurrentSubscriptionPlan;
  onSelectPlan: (plan: OrderedPlan) => Promise<void>;
  toggleDrawer: () => void;
  allProducts: ProductPlan[];
  canChangeSubscription: boolean;
  billingDateToday: boolean;
  hasDeferment: boolean;
}

/**
 * Navigation priority (highest to lowest):
 * 1. externalUrl - redirects to an external page (full page navigation)
 * 2. routePath - navigates to an internal route via react-router
 */
interface ActionCardItem {
  title: string;
  desc: string;
  icon: React.ReactNode;
  routePath?: string;
  externalUrl?: string;
  onActivate?: () => void;
  testId: string;
  onMobileAppActivate?: () => void;
}

const ManageSubscriptionNav: React.FC<ManageSubscriptionNavProps> = ({
  isInCoolingOffPeriod,
  currentSubscription,
  onSelectPlan,
  toggleDrawer,
  allProducts,
  canChangeSubscription,
  billingDateToday,
  hasDeferment,
}) => {
  const history = useHistory();
  const customerNumber = useSelector((state: any) => state.auth?.currentUser?.customerNumber);
  const currentFamily = normalizeProductFamily(currentSubscription?.planCode ?? '');
  const isAnnualUser = isAnnually(currentSubscription?.termFrequency);
  const splits = useSplits([splitsList.annualPricing]) as Record<string, boolean>;
  const isAnnualPricingEnabled = !!splits[splitsList.annualPricing];
  const basicOffer = allProducts.find(o => o.family === flexProductFamilies.basic);

  const { annualPlan, hasBothBillingPeriods } = getFamilyBillingPeriods(currentFamily, allProducts);
  const annualDiscountPercentage = annualPlan?.discountPercentage;

  const handleCancelSubscription = () => {
    if (!basicOffer) return;

    void onSelectPlan({
      id: flexProductFamilies.basic,
      family: flexProductFamilies.basic,
      cardTitle: 'Free account',
      cardSubtitle: '',
      product: basicOffer,
      priceSummary: 'Free',
      isCurrent: false,
      tabLabel: 'Free',
    });
  };

  const changeBillingPeriodCard: ActionCardItem = {
    title: isAnnualUser ? 'Change to monthly billing' : 'Change to annual billing',
    desc: (() => {
      if (isAnnualUser) return 'Change to monthly payments at your next billing date.';
      return annualDiscountPercentage
        ? `Save ${annualDiscountPercentage}% when you switch to annual billing.`
        : 'Switch to annual billing.';
    })(),
    icon: <CalendarIcon iconSize={iconSizes.medium} accentColor="black" />,
    routePath: `/switch?switchingTo=${currentFamily}&${queryParams.billingPeriod}=${
      isAnnualUser ? 'monthly' : 'annual'
    }`,
    testId: 'action-card-change-billing-period',
    onMobileAppActivate: () => {
      const targetLocation = isAnnualUser
        ? MobileLocations.CHANGE_TO_MONTHLY_BILLING
        : MobileLocations.CHANGE_TO_ANNUAL_BILLING;
      navigateTo(targetLocation);
    },
  };

  const cancelSubscriptionCard: ActionCardItem = {
    title: 'Cancel subscription',
    desc: isInCoolingOffPeriod ? 'Switch to Free account' : 'Switch to the Free account at your next billing date.',
    icon: <SettingsIcon />,
    onActivate: handleCancelSubscription,
    testId: 'action-card-cancel-subscription',
    onMobileAppActivate: () => {
      navigateTo(MobileLocations.CANCEL_SUBSCRIPTION);
    },
  };

  const sharedCards: ActionCardItem[] = [
    ...(canChangeSubscription && !hasDeferment && basicOffer ? [cancelSubscriptionCard] : []),
    ...(billingDateToday || hasDeferment
      ? []
      : [
          {
            title: 'Payment and billing',
            desc: 'Change your payment method or frequency.',
            icon: <img src={CreditCardIcon} alt="credit card icon" />,
            routePath: '/billing',
            testId: 'action-card-payment-billing',
            onMobileAppActivate: () => {
              navigateTo(MobileLocations.PAYMENT_AND_BILLING);
            },
          },
        ]),
    {
      title: 'Contact us',
      desc: customerNumber ? `Your customer number: ${customerNumber}` : 'Contact our support team for help.',
      icon: <ContactSupportIcon iconSize={iconSizes.medium} />,
      externalUrl: `${getEnv().REACT_APP_INS_URL}/contact`,
      testId: 'action-card-contact-us',
      onMobileAppActivate: () => {
        navigateTo(MobileLocations.CONTACT_US);
      },
    },
  ];
  const actionCards: ActionCardItem[] =
    canChangeSubscription && !hasDeferment && hasBothBillingPeriods && isAnnualPricingEnabled
      ? [changeBillingPeriodCard, ...sharedCards]
      : sharedCards;

  const handleCardClick =
    (card: ActionCardItem) => (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | null>) => {
      e.preventDefault();

      if (isMobileApp() && card.onMobileAppActivate) {
        card.onMobileAppActivate();
        toggleDrawer();
        return;
      }

      if (card.externalUrl) {
        globalThis.location.href = card.externalUrl;
        return;
      }

      if (card.onActivate) {
        card.onActivate();
        return;
      }

      if (card.routePath) {
        history.push(card.routePath);
      }
    };

  return (
    <>
      <DrawerHeading>Manage subscription</DrawerHeading>
      <ActionCardContainer>
        {actionCards.map(card => (
          <InfoCardExtended
            id={card.testId}
            key={card.testId}
            title={card.title}
            description={card.desc}
            icon={card.icon}
            testId={card.testId}
            href="#"
            onClick={handleCardClick(card)}
          />
        ))}
      </ActionCardContainer>
    </>
  );
};
export default ManageSubscriptionNav;
